import { Link } from 'react-router';
import { Button, Icon } from '@efira/ui';

import { FOOTER_HEIGHT } from './geometry';
import { Connection } from './left/Connection';
import { OverlayLinkRow } from './left/OverlayLinkRow';
import { GamePanel } from './left/game/GamePanel';
import { SessionControls } from './left/SessionControls';
import { Simulator } from './left/Simulator';
import { StreamSettings } from './left/StreamSettings';
import { useConsoleSession } from './session';

/**
 * Левая колонка: управление. Порядок секций - карта экранов, раздел 4.
 *
 * Колонка прокручивается ОТДЕЛЬНО от страницы: подвал с тремя кнопками стоит
 * на месте, прокручивается только тело. Страница целиком не прокручивается
 * нигде в консоли - иначе стример теряет из виду кадр.
 */
export function LeftColumn({ onGuide }: { onGuide: () => void }) {
  const session = useConsoleSession();

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        data-console-scroll="left"
        tabIndex={0}
        role="region"
        aria-label="Управление"
        className="min-h-0 flex-1 overflow-y-auto"
      >
        <Connection />
        {/* Ссылка оверлея - сразу под подключением и только до первого
            подключения оверлея (решение владельца 2026-09-26, п. 4). */}
        <OverlayLinkRow />
        {/*
          СИМУЛЯТОР И УПРАВЛЕНИЕ - ТОЛЬКО ПРИ ВЫБРАННОЙ ИГРЕ.
          У эталона без игры в колонке видны лишь подключение, настройки эфира,
          промо и подвал: паузить и перезапускать нечего, а кнопка, которая
          ничего не делает, читается как поломка.
        */}
        {session.game !== null && (
          <>
            <Simulator />
            <SessionControls />
          </>
        )}
        <StreamSettings />
        {/*
          Панель игры: название со сбросом, сведения, привязки, тестовые
          действия и настройки. Прежде здесь стояли четыре заглушки, а сам
          экран привязок жил под `/app/legacy/bindings` - чтобы поменять
          порог, приходилось уходить с консоли и терять кадр из виду.
        */}
        {session.game !== null && <GamePanel />}
      </div>

      {/* Подвал закреплён: он не уезжает вместе с прокруткой тела колонки. */}
      <div
        data-control-row="left-footer"
        /*
          ПОДПИСИ ВСЕГДА, ПОДВАЛ ПЕРЕНОСИТСЯ ВО ВТОРОЙ РЯД. Находка N-08
          повторного аудита и блок 7 единицы 21: подписи прятались по
          порогам 460/520/560/600 px, а левая колонка на заводской ширине
          таких размеров не бывает - четыре значка из пяти были безымянны
          всегда.

          Выбор по замеру: пять подписей с отступами просят ~574 px. Заводская
          колонка - 317 px на окне 1024, 455 на 1470, 594 на 1920. Меню «Ещё»
          спрятало бы как раз промо, которое решение владельца 2026-09-26 (3)
          перенесло сюда, чтобы его видели; два ряда держат все пять подписей
          на любой заводской ширине и даже на `leftMin` 240 px (самая длинная
          подпись - 153 px). Ворота - `e2e/class-footer.spec.ts`: ширины и
          безымянные органы печатаются.
        */
        className="@container border-foreground/10 flex shrink-0 flex-wrap items-center gap-1 border-t px-2 py-1"
        style={{ minHeight: FOOTER_HEIGHT }}
      >
        {/*
          Подвал эталона: гид, документация, сообщить о проблеме. Настроек
          здесь нет - они в меню аккаунта, и дублировать вход значит разводить
          два места для одного действия.
        */}
        <Button
          variant="ghost"
          size="s"
          className="gap-2 px-2"
          data-left="footer-guide"
          onClick={onGuide}
        >
          <Icon name="info" size="sm" />
          Гид
        </Button>
        {/* ТРИ КНОПКИ ПОДВАЛА - ОДНОГО ВИДА. «Проблема» была крупнее
            «Гида» и «Документации» (находка владельца 2026-09-25): у неё
            не было размера кнопки вовсе, она рисовалась как ссылка. */}
        <Button asChild variant="ghost" size="s" className="gap-2 px-2">
          <a
            href="https://efira.live/docs"
            target="_blank"
            rel="noreferrer"
            data-left="footer-docs"
          >
            <Icon name="open" size="sm" />
            Документация
          </a>
        </Button>
        {/*
          ПРОМО ПЕРЕЕХАЛО СЮДА С ПЕРВОГО ЭКРАНА.

          Находка аудита единицы 16 (12): треть первого экрана инструмента
          занимали три рекламные карточки, и рабочих органов на этом экране
          было меньше, чем промо. Решение владельца 2026-09-26 (3):
          «Предложить игру» и Discord - в подвал, тариф Pro - в шапку и в
          меню аккаунта (он там и был, двумя входами сразу).

          В подвале они - значок с подписью, а не карточка. Подпись видна
          всегда: узкая колонка переносит подвал во второй ряд.
        */}
        <Button asChild variant="ghost" size="s" className="gap-2 px-2">
          <Link to="/app/feedback/idea" data-left="suggest-game">
            <Icon name="games" size="sm" />
            Предложить игру
          </Link>
        </Button>
        <Button asChild variant="ghost" size="s" className="gap-2 px-2">
          <a
            href="https://discord.gg/efira"
            target="_blank"
            rel="noreferrer"
            data-left="discord-card"
            title="Сообщество Efira Live в Discord"
          >
            <Icon name="people" size="sm" />
            Discord
          </a>
        </Button>
        <Button asChild variant="ghost" size="s" className="gap-2 px-2">
          <Link to="/app/feedback/issue" data-left="footer-report" title="Сообщить о проблеме">
            <Icon name="warning" size="sm" />
            Проблема
          </Link>
        </Button>
      </div>
    </div>
  );
}
